import { usePrevious } from "@redotech/react-util/hook";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { AskRedoDisclosureButton } from "./ask-redo-disclosure-button";
import * as styles from "./ask-redo-reasoning.module.css";

/**
 * Collapsible "thinking" panel.
 *
 * Default behaviour:
 *   - While reasoning is streaming → open
 *   - Once reasoning is done       → closed
 *
 * Manual overrides win until the streaming state itself flips. That
 * way, a merchant who expands "Thought" to re-read it can do so
 * without it auto-collapsing on the next render, but they don't get
 * stuck staring at a permanently-open reasoning block from three
 * turns ago: every transition into a fresh "Thinking…" cycle clears
 * the override and resumes the default.
 */
export const AskRedoReasoning = memo(function AskRedoReasoning({
  text,
  isStreaming,
}: {
  text: string;
  isStreaming: boolean;
}) {
  const [override, setOverride] = useState<boolean | undefined>(undefined);

  // Reset the override whenever the streaming state actually
  // changes — that's the only signal that "the default just
  // shifted, so the user's manual choice is stale". React supports
  // a state update during render of the same component for exactly
  // this "store info from a previous render" pattern.
  const previousStreaming = usePrevious(isStreaming, isStreaming);
  if (previousStreaming !== isStreaming && override !== undefined) {
    setOverride(undefined);
  }

  const isOpen = override ?? isStreaming;

  return (
    <div className={styles.wrapper}>
      <AskRedoDisclosureButton
        isOpen={isOpen}
        onToggle={() => setOverride(!isOpen)}
      >
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {isStreaming ? "Thinking…" : "Thought"}
        </Text>
      </AskRedoDisclosureButton>
      {isOpen && (
        <div className={styles.content}>
          <Text fontSize="xs" textColor="secondary">
            {text}
          </Text>
        </div>
      )}
    </div>
  );
});
